import { useState, type JSX } from 'react';
import { z } from 'zod';
import type { BotScope } from '@cryptology-assist/domain';
import { botPath, idSchema, request, type Session } from './api';
import { Field, ErrorNotice, useMutation } from './components';
import type { Copy } from './copy';

const captureSchema = z.object({
  id: z.string(),
  source: z.string(),
  type: z.string(),
  content: z.string(),
  originalDigest: z.string(),
  capturedAt: z.string(),
});
async function fileBase64(file: File): Promise<string> {
  if (file.size > 2_000_000) throw new Error('KNOWLEDGE_FILE_SIZE_INVALID');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('KNOWLEDGE_FILE_READ_FAILED'));
    reader.onabort = () => reject(new Error('KNOWLEDGE_FILE_READ_CANCELLED'));
    reader.onload = () => {
      const value = String(reader.result).split(',')[1];
      if (!value) reject(new Error('KNOWLEDGE_FILE_EMPTY'));
      else resolve(value);
    };
    reader.readAsDataURL(file);
  });
}
export function KnowledgeImport({
  scope,
  session,
  copy,
  onPublished,
  initialSource = '',
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  onPublished: () => void;
  initialSource?: string;
}): JSX.Element {
  const [source, setSource] = useState(initialSource),
    [file, setFile] = useState<File | null>(null),
    [capture, setCapture] = useState<z.infer<typeof captureSchema> | null>(
      null
    );
  const mutation = useMutation();
  return (
    <section className="form-section">
      <h2>{copy.importKnowledge}</h2>
      <p>{copy.importKnowledgeHelp}</p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void mutation.run(async () => {
            const body = file
              ? {
                  kind: 'document',
                  source,
                  contentType:
                    file.type ||
                    (file.name.endsWith('.md')
                      ? 'text/markdown'
                      : file.name.endsWith('.txt')
                        ? 'text/plain'
                        : 'application/octet-stream'),
                  base64: await fileBase64(file),
                }
              : { kind: 'website', source };
            setCapture(
              await request(
                botPath(scope, 'knowledge/capture'),
                captureSchema,
                { csrf: session.csrf, body }
              )
            );
          });
        }}
      >
        <Field label={copy.source}>
          <input
            required
            type="url"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          />
        </Field>
        <Field label={copy.sourceDocument}>
          <input
            type="file"
            accept=".pdf,.txt,.md,.html,text/plain,text/markdown,text/html,application/pdf"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </Field>
        <button type="submit" disabled={mutation.busy}>
          {copy.captureSource}
        </button>
      </form>
      <ErrorNotice error={mutation.error} copy={copy} />
      {capture && (
        <div>
          <p>{capture.source}</p>
          <p>{copy.reviewImportedSource}</p>
          <textarea
            readOnly
            rows={14}
            value={capture.content}
            aria-label={copy.content}
          />
          <button
            disabled={mutation.busy}
            onClick={() =>
              void mutation.run(async () => {
                await request(
                  botPath(scope, `knowledge/imports/${capture.id}`),
                  idSchema,
                  { csrf: session.csrf, body: {} }
                );
                setCapture(null);
                onPublished();
              })
            }
          >
            {copy.publish}
          </button>
        </div>
      )}
    </section>
  );
}
